<script lang="ts" setup>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import type { MemberPageConfig } from '../configs/types'
import { useUserStore } from '@/stores'
import { usePurchase, useSubscription } from '@/composables/payment'
import { tripartiteLoginPage } from '@/composables/useTripartiteLogin'
import { useElementClick } from '@/composables/tracks/useElementClick'
import { VIP_TYPE } from '@/constants'
import { theme as brandTheme } from '@/config/brands'
import { t } from '@/i18n/utils'

interface Props {
  config: MemberPageConfig
}

const props = defineProps<Props>()
const router = useRouter()
const route = useRoute()

// ===== 逻辑层：与其他品牌组件完全一致，不做任何删减 =====
const userStore = useUserStore()
const { elementClick } = useElementClick()
const { subscribeProducts } = useSubscription('member')
const { templateProducts, loading } = usePurchase('member')

const topupShow = computed(() => templateProducts.value && templateProducts.value.length > 0)

// 未绑定三方账号时展示 Guest
const displayName = computed(() =>
  userStore.$state.thirdNickName
  || userStore.$state.email
  || userStore.$state.memberName
  || t('Guest'),
)

// 登录逻辑保留（NovelMello 站点无登录入口，showLoginBtn=false 时该按钮不渲染）
function login() {
  elementClick('login')
  tripartiteLoginPage('Profile')
}

function navigateTo(name: string) {
  if (route.name !== name)
    router.push({ name })
}

function handleTopupOrWelfare() {
  navigateTo(topupShow.value ? 'pay' : 'welfare')
}

// ===== 菜单数组化：4 项，设计稿无 Be a Writer =====
const menuItems = computed(() => [
  {
    key: 'recharge',
    label: props.config.texts.transactionHistory,
    icon: 'receipt',
    tone: 'pink' as const,
    onClick: () => navigateTo('recharge'),
  },
  {
    key: 'consume',
    label: props.config.texts.chaptersUnlocked,
    icon: 'list-checks',
    tone: 'purple' as const,
    onClick: () => navigateTo('consume'),
  },
  {
    key: 'contact-us',
    label: props.config.texts.contactUs,
    icon: 'mail',
    tone: 'pink' as const,
    onClick: () => {
      elementClick('contact_us')
      navigateTo('contact-us')
    },
  },
  {
    key: 'setting',
    label: props.config.texts.setting,
    icon: 'settings',
    tone: 'purple' as const,
    onClick: () => {
      elementClick('setting')
      navigateTo('setting')
    },
  },
])

// ===== 样式变量：优先品牌主题，字面量仅作兜底 =====
const pink = brandTheme.primaryColor || '#F0509B'
const purple = '#7C4DE8'
const pinkSoft = '#FDEFF6'
const purpleSoft = '#F3EDFE'
const gold = '#FFB23E'
const gradient = brandTheme.primaryGradient || 'linear-gradient(90deg, #F0509B 0%, #9B5CF0 100%)'

const bgGlobal = brandTheme.bgGlobal || '#FBF6FB'
const bgCard = brandTheme.bgCard || '#FFFFFF'
const ink1 = brandTheme.textPrimary || '#2B1B34'
const ink2 = brandTheme.textSecondary || '#71607E'
const ink3 = brandTheme.textTertiary || '#AB9BB8'

const headerBg = props.config.theme.headerBg || 'linear-gradient(150deg, #FDE0EF 0%, #EDE4FD 100%)'
const vipBannerBg = props.config.theme.subscribeBg || 'linear-gradient(100deg, #FDE8F1 0%, #EDE4FD 100%)'
const vipTitleColor = props.config.theme.subscribeText || ink1
const vipSubtitleColor = props.config.theme.subscribeText2 || purple
const dividerColor = props.config.theme.dividerColor || '#F2EAF4'
const cardShadow = props.config.theme.cardShadow || '0 4px 16px rgba(124, 77, 232, 0.08)'
const pillBg = props.config.theme.topUpBtnBg || gradient
const vipPillBg = props.config.theme.vipBtnBg || gradient
</script>

<template>
  <div class="nm-page">
    <!-- 头部：浅渐变 + 白圆头像 + 昵称/UID -->
    <div class="nm-header">
      <div class="nm-user">
        <div class="nm-avatar">
          <img
            v-if="userStore.$state.headImageUrl"
            class="nm-avatar-img"
            :src="userStore.$state.thirdHeadImgUrl"
            alt="avatar"
          >
          <img
            v-else-if="config.resources.userDefaultAvatar"
            class="nm-avatar-img"
            :src="config.resources.userDefaultAvatar"
            alt="avatar"
          >
          <i-lucide-user v-else class="nm-avatar-icon" />
        </div>
        <div class="nm-user-info">
          <p class="nm-username">
            {{ displayName }}
          </p>
          <p class="nm-uid">
            UID {{ userStore.$state.code }}
          </p>
          <p v-if="userStore.$state.subscribe" class="nm-vip-badge">
            <img
              v-if="userStore.$state.subscribeType === VIP_TYPE.DISCOUNT"
              :src="config.resources.memberVipDiscount"
              class="h-13px w-auto"
            >
            <img v-else :src="config.resources.memberVip" class="h-13px w-auto">
          </p>
        </div>
      </div>

      <!-- NovelMello 无登录入口：配置 showLoginBtn=false 关闭渲染，登录逻辑保留 -->
      <div v-if="config.showLoginBtn !== false && !userStore.$state.thirdPartType">
        <a class="nm-login-btn" @click.stop="login">
          {{ config.texts.signIn }}
        </a>
      </div>
    </div>

    <div class="nm-body">
      <!-- 钱包白卡 -->
      <div class="nm-card nm-wallet">
        <div class="nm-wallet-row">
          <div class="nm-wallet-left">
            <i-lucide-wallet class="nm-wallet-icon" />
            <span class="nm-wallet-title">{{ config.texts.myWallet }}</span>
          </div>
          <div class="nm-wallet-right">
            <strong class="nm-coin-value">{{ userStore.$state.coinBalance }}</strong>
            <span class="nm-coin-unit">{{ config.texts.coins }}</span>
            <div v-if="!loading" class="nm-pill" @click="elementClick('TopUp'); handleTopupOrWelfare()">
              {{ topupShow ? config.texts.topUp : config.texts.get }}
            </div>
          </div>
        </div>

        <!-- 卡内 VIP banner -->
        <div
          v-if="subscribeProducts?.length || userStore.$state.subscribe"
          class="nm-vip"
        >
          <i-lucide-crown class="nm-vip-crown" />
          <div class="nm-vip-text">
            <div class="nm-vip-title">
              <span v-if="!userStore.$state.subscribe">{{ config.texts.vipTitle || config.texts.getVip }}</span>
              <span v-else>{{ config.texts.vipMembership }}</span>
            </div>
            <div v-if="!userStore.$state.subscribe" class="nm-vip-subtitle">
              {{ config.texts.vipSubtitle || config.texts.moreCoinsMoreBenefits }}
            </div>
            <div v-if="userStore.$state.subscribeEndDate" class="nm-vip-subtitle">
              {{ config.texts.expires }} {{ $d(new Date(userStore.$state.subscribeEndDate), 'short') }}
            </div>
          </div>
          <div
            v-if="!userStore.$state.subscribe"
            class="nm-pill nm-pill-vip"
            @click="elementClick('Details'); navigateTo('subscribe')"
          >
            {{ config.texts.details }}
          </div>
        </div>
      </div>

      <!-- 菜单白卡：4 行，无 Be a Writer -->
      <div class="nm-card nm-menu">
        <div
          v-for="(item, index) in menuItems"
          :key="item.key"
          class="nm-menu-item"
          :class="{ 'nm-menu-divider': index > 0 }"
          @click="item.onClick"
        >
          <div class="nm-menu-left">
            <div class="nm-menu-icon" :class="item.tone === 'pink' ? 'nm-icon-pink' : 'nm-icon-purple'">
              <i-lucide-receipt v-if="item.icon === 'receipt'" class="nm-menu-icon-svg" />
              <i-lucide-list-checks v-else-if="item.icon === 'list-checks'" class="nm-menu-icon-svg" />
              <i-lucide-mail v-else-if="item.icon === 'mail'" class="nm-menu-icon-svg" />
              <i-lucide-settings v-else class="nm-menu-icon-svg" />
            </div>
            <span class="nm-menu-label">{{ item.label }}</span>
          </div>
          <i-lucide-chevron-right class="nm-menu-arrow" />
        </div>
      </div>

      <!-- 浮动胶囊 Tabbar 占位 -->
      <div class="h-92px" />
    </div>

    <BasicTabbar tab="member" />
  </div>
</template>

<style scoped>
.nm-page {
  width: 100%;
  min-height: 100vh;
  background: v-bind(bgGlobal);
  box-sizing: border-box;
}

/* ===== 头部 ===== */
.nm-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 22px 20px 28px;
  background: v-bind(headerBg);
  box-sizing: border-box;
}

.nm-user {
  display: flex;
  align-items: center;
  min-width: 0;
}

.nm-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 14px;
  overflow: hidden;
}

.nm-avatar-img {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
}

.nm-avatar-icon {
  width: 28px;
  height: 28px;
  color: v-bind(pink);
}

.nm-user-info {
  min-width: 0;
}

/* nm-font-title 由 htmls/novelmello.html 提供（Baloo 2），此处兜底字体栈 */
.nm-username {
  font-family: var(--nm-font-display, 'Baloo 2', 'Nunito Sans', system-ui, -apple-system, sans-serif);
  font-size: 20px;
  font-weight: 700;
  line-height: 26px;
  color: v-bind(ink1);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 200px;
}

.nm-uid {
  margin-top: 2px;
  font-size: 12px;
  line-height: 16px;
  color: v-bind(ink2);
}

.nm-vip-badge {
  display: flex;
  align-items: center;
  margin-top: 4px;
}

/* NovelMello 默认不渲染；其他配置开启 showLoginBtn 时保持可用样式 */
.nm-login-btn {
  display: inline-block;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 800;
  color: #FFFFFF;
  background: v-bind(gradient);
  border-radius: 999px;
  cursor: pointer;
}

/* ===== 内容区 ===== */
.nm-body {
  padding: 16px;
  box-sizing: border-box;
}

.nm-card {
  background: v-bind(bgCard);
  border-radius: 20px;
  box-shadow: v-bind(cardShadow);
  box-sizing: border-box;
}

/* ===== 钱包卡 ===== */
.nm-wallet {
  padding: 16px;
  margin-bottom: 16px;
}

.nm-wallet-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.nm-wallet-left {
  display: flex;
  align-items: center;
  min-width: 0;
}

.nm-wallet-icon {
  width: 20px;
  height: 20px;
  color: v-bind(pink);
  margin-right: 8px;
  flex-shrink: 0;
}

.nm-wallet-title {
  font-size: 15px;
  font-weight: 800;
  line-height: 20px;
  color: v-bind(ink1);
}

.nm-wallet-right {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.nm-coin-value {
  font-family: var(--nm-font-display, 'Baloo 2', 'Nunito Sans', system-ui, -apple-system, sans-serif);
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
  color: v-bind(ink1);
}

.nm-coin-unit {
  margin-left: 4px;
  font-size: 12px;
  line-height: 16px;
  color: v-bind(ink3);
}

.nm-pill {
  margin-left: 12px;
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 800;
  line-height: 14px;
  color: #FFFFFF;
  background: v-bind(pillBg);
  border-radius: 999px;
  white-space: nowrap;
  cursor: pointer;
  flex-shrink: 0;
}

.nm-pill-vip {
  background: v-bind(vipPillBg);
}

/* ===== 卡内 VIP banner ===== */
.nm-vip {
  display: flex;
  align-items: center;
  margin-top: 14px;
  padding: 12px;
  border-radius: 14px;
  background: v-bind(vipBannerBg);
  box-sizing: border-box;
}

.nm-vip-crown {
  width: 18px;
  height: 18px;
  color: v-bind(gold);
  flex-shrink: 0;
  margin-right: 8px;
}

.nm-vip-text {
  flex: 1;
  min-width: 0;
}

.nm-vip-title {
  font-size: 15px;
  font-weight: 800;
  line-height: 20px;
  color: v-bind(vipTitleColor);
}

.nm-vip-subtitle {
  margin-top: 2px;
  font-size: 12px;
  line-height: 16px;
  color: v-bind(vipSubtitleColor);
}

/* ===== 菜单卡 ===== */
.nm-menu {
  padding: 4px 16px;
}

.nm-menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 0;
  cursor: pointer;
}

.nm-menu-divider {
  border-top: 1px solid v-bind(dividerColor);
}

.nm-menu-left {
  display: flex;
  align-items: center;
  min-width: 0;
}

.nm-menu-icon {
  width: 34px;
  height: 34px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 12px;
}

.nm-icon-pink {
  background: v-bind(pinkSoft);
  color: v-bind(pink);
}

.nm-icon-purple {
  background: v-bind(purpleSoft);
  color: v-bind(purple);
}

.nm-menu-icon-svg {
  width: 17px;
  height: 17px;
}

.nm-menu-label {
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: v-bind(ink1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nm-menu-arrow {
  width: 16px;
  height: 16px;
  color: v-bind(ink3);
  flex-shrink: 0;
  margin-left: 8px;
}
</style>
